import { Fragment, useEffect, useState } from 'react';
import './front-office.styles.scss';
import { Link } from 'react-router-dom';
import { MAX_YEARS, askingContract, capYear, formatMoney, maxSalary, moneyRules, payroll } from '@shared/contracts.js';
import { FA_DAYS, PITCHES_PER_DAY, PITCH_TYPES, faDay, resignWindowOpen } from '@shared/portal.js';
import { listenToDoc } from '@/utils/getDataFromDb';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { positions } from '@/utils/positions';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

const SHOWN = 25;
const FA_PITCHES = PITCH_TYPES.filter(p => p.key !== 'prestige');
const LEVEL_ORDER = { high: 0, medium: 1, low: 2 };

// Pro front office: payroll and cap room, re-signing your expiring players,
// the free agent pool (offers and pitches in the offseason, signing during
// the season) and releasing players. myTeam: the manager's team document;
// without it (admins) the page is read-only.
const FrontOffice = ({ league, teams, teamLink, myTeam }) => {

    const [ fa, setFa ] = useState(undefined);
    const [ log, setLog ] = useState(null);
    const [ pos, setPos ] = useState(0);
    const [ shown, setShown ] = useState(SHOWN);
    const [ busy, setBusy ] = useState(null);
    const [ replies, setReplies ] = useState({});
    const [ confirm, setConfirm ] = useState(null);
    // A free agent the manager is about to sign (in season).
    const [ signing, setSigning ] = useState(null);
    // Your roster's order: 'overall' or 'position'.
    const [ rosterSort, setRosterSort ] = useState('overall');
    const [ alertMessage, setAlertMessage ] = useState(null);

    useEffect(() => {
        const listeners = [];
        listenToDoc({ 'path': `leagues/${league.id}/summary/freeAgents`, 'callback': setFa, 'handleListener': (u) => listeners.push(u) });
        listenToDoc({ 'path': `leagues/${league.id}/summary/faPitches`, 'callback': setLog, 'handleListener': (u) => listeners.push(u) });
        return () => listeners.forEach(u => u());
    }, [league.id])

    const rules = moneyRules(league.settings);
    if (!rules) return <div className='g-card'><p className='meta'>College leagues have no contracts.</p></div>;
    if (fa === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;

    const year = capYear(league);
    const day = faDay(league);
    const resigning = resignWindowOpen(league);
    const inSeason = !['offseason', 'complete'].includes(league.season.phase);
    const myId = myTeam?.id;
    const myOffers = Object.values(fa?.players ?? {}).filter(c => c.status === 'available' && c.offers?.[myId]);
    const pay = myTeam ? payroll(myTeam, year) : 0;
    const pending = myOffers.reduce((s, c) => s + c.offers[myId].salary, 0);
    const room = rules.cap - pay - pending;
    const counted = fa?.pitchCounts?.[myId];
    const pitchesLeft = PITCHES_PER_DAY - (counted?.day === day ? counted.used : 0);
    // Releases: during the season, or the offseason once development has run.
    const canRelease = inSeason || (league.season.phase === 'offseason' && league.season.offseason?.stage !== 'review');
    const cards = Object.values(fa?.players ?? {})
        .filter(c => c.status === 'available' && (!pos || c.pos1 === pos))
        .sort((a, b) => b.overall - a.overall || a.lName.localeCompare(b.lName));

    async function run(key, command, data, onDone) {
        setBusy(key);
        const res = await UseCloudFunction(command, { leagueId: league.id, ...data });
        setBusy(null);
        if (res.error) {
            setAlertMessage(res.error);
            return;
        }
        if (onDone) onDone(res);
    }

    return (
        <div className='front-office'>
            <div className='g-card office-head'>
                <h3>Front office</h3>
                <p>{headline(league, day, resigning, inSeason)}</p>
                {myTeam &&
                    <dl className='money-list'>
                        <dt>Payroll (Season {year})</dt><dd>{formatMoney(pay)} of {formatMoney(rules.cap)}</dd>
                        {pending > 0 && <><dt>Your open offers</dt><dd>{formatMoney(pending)}</dd></>}
                        <dt>Cap room</dt><dd className={room < 0 ? 'over' : ''}>{formatMoney(room)}</dd>
                        <dt>Roster</dt><dd>{Object.keys(myTeam.players).length} players (allowed {rules.rosterMin}-{rules.rosterMax})</dd>
                        {(myTeam.deadMoney ?? []).filter(d => d.year === year).map(d => (
                            <Fragment key={d.playerId}><dt>Released</dt><dd>{d.name}: {formatMoney(d.salary)} this season</dd></Fragment>
                        ))}
                    </dl>
                }
                {day > 0 && myTeam && <p className='meta'>{Math.max(0, pitchesLeft)} of {PITCHES_PER_DAY} pitches left today. Players start signing on day 3; after day {FA_DAYS}, everyone with a fair offer signs.</p>}
            </div>

            {resigning && myTeam && <ResignList league={league} team={myTeam} rules={rules} busy={busy} run={run} />}

            <div className='g-card'>
                <h3>Free agents</h3>
                <p className='meta'>
                    {inSeason ? 'Sign a free agent for the rest of the season at their asking price (one year).' :
                        day ? 'Make offers within your cap room. Each player weighs money against playing time, winning and development, and their replies to your pitches hint at what they care about.' :
                        'Free agency opens after the re-signing days and the draft.'}
                </p>
                <div className='chips' role='tablist' aria-label='Position'>
                    {[0, 1, 2, 3, 4, 5].map(n => (
                        <button key={n} role='tab' aria-selected={pos === n} className={`chip ${pos === n ? 'active' : ''}`} onClick={() => { setPos(n); setShown(SHOWN); }}>{n ? positions[n - 1].short : 'All'}</button>
                    ))}
                </div>
            </div>

            {cards.length === 0 ? <div className='g-card'><p className='meta'>No free agents{pos ? ' at this position' : ''} right now.</p></div> :
                <div className='fa-list'>
                    {cards.slice(0, shown).map(c => (
                        <FaCard key={c.id} c={c} teams={teams} teamLink={teamLink} myTeam={myTeam} rules={rules}
                            day={day} inSeason={inSeason} room={room} pitchesLeft={pitchesLeft} busy={busy} run={run} onSign={setSigning}
                            log={(log?.log ?? []).filter(l => l.teamId === myId && l.playerId === c.id)}
                            reply={replies[c.id]} setReply={(r) => setReplies({ ...replies, [c.id]: r })} />
                    ))}
                    {cards.length > shown && <button className='g-button' onClick={() => setShown(shown + SHOWN)}>Show more ({cards.length - shown} left)</button>}
                </div>
            }

            {myTeam &&
                <div className='g-card roster-card'>
                    <h3>Your roster</h3>
                    <p className='meta'>
                        {Object.keys(myTeam.players).length} players.
                        {canRelease ? ' A released player joins the free agent pool; the salary still counts against your cap this season.' : ''}
                    </p>
                    <div className='sort-tabs' role='radiogroup' aria-label='Sort by'>
                        {[['overall', 'Overall'], ['position', 'Position']].map(([key, label]) => (
                            <button key={key} role='radio' aria-checked={rosterSort === key} className={`sort-tab ${rosterSort === key ? 'active' : ''}`} onClick={() => setRosterSort(key)}>{label}</button>
                        ))}
                    </div>
                    <ul className='release-list'>
                        {Object.values(myTeam.players)
                            .sort((a, b) => (rosterSort === 'position' ? a.pos1 - b.pos1 : 0) || b.overall - a.overall || a.lName.localeCompare(b.lName))
                            .map((p, i, list) => (
                            <Fragment key={p.id}>
                                {rosterSort === 'position' && list[i - 1]?.pos1 !== p.pos1 &&
                                    <li className='pos-heading'>{positions[p.pos1 - 1]?.long ?? positions[p.pos1 - 1]?.short}</li>}
                                <li>
                                <span className='roster-ovr'>{p.overall}</span>
                                <span className='roster-who'>
                                    <b>{p.fName} {p.lName}</b>
                                    <span className='meta'>{positions[p.pos1 - 1]?.short}{p.pos2 ? `/${positions[p.pos2 - 1]?.short}` : ''} · age {p.age}{p.contract ? ` · ${formatMoney(p.contract.salary)}${p.contract.years ? ` × ${p.contract.years}` : ', expiring'}` : ''}</span>
                                </span>
                                {canRelease &&
                                    <button className={`g-button small-btn ${busy ? 'disabled' : ''}`} disabled={Boolean(busy)} onClick={() => setConfirm(p)}>
                                        {busy === `release-${p.id}` ? <ThreeDotsLoader /> : 'Release'}
                                    </button>
                                }
                                </li>
                            </Fragment>
                        ))}
                    </ul>
                </div>
            }

            <ModalConfirm
                show={Boolean(confirm)}
                cancel={() => setConfirm(null)}
                message={confirm ? `Release ${confirm.fName} ${confirm.lName}? The player joins the free agent pool, and the salary still counts on your cap this season.` : ''}
                onApprove={() => { const p = confirm; setConfirm(null); run(`release-${p.id}`, 'rosterMove', { playerId: p.id, action: 'release' }); }} />
            <ModalConfirm
                show={Boolean(signing)}
                cancel={() => setSigning(null)}
                message={signing ? `Sign ${signing.fName} ${signing.lName} for ${formatMoney(signing.ask.salary)} for the rest of the season (one year)? That leaves ${formatMoney(room - signing.ask.salary)} of cap room.` : ''}
                onApprove={() => { const c = signing; setSigning(null); run(`sign-${c.id}`, 'rosterMove', { playerId: c.id, action: 'sign' }); }} />
            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
        </div>
    )
}

function headline(league, day, resigning, inSeason) {
    if (inSeason) return 'The season is on: you can sign free agents and release players.';
    if (resigning) return 'Re-signing: offer your expiring players new contracts before free agency opens. Each player answers right away, one offer a day.';
    if (day) return `Free agency, day ${day} of ${FA_DAYS}.`;
    if (league.season.offseason?.stage === 'fa-close') return 'Free agency is over. Unsigned players stay available during the season.';
    return 'After the season is in the record books, players develop, then you get two days to re-sign your own players before free agency.';
}

// Your expiring players during the re-signing days.
const ResignList = ({ league, team, rules, busy, run }) => {
    const [ answers, setAnswers ] = useState({});
    const expiring = Object.values(team.players).filter(p => p.contract?.years === 0)
        .sort((a, b) => b.overall - a.overall);
    return (
        <div className='g-card'>
            <h3>Re-sign your players</h3>
            {expiring.length === 0 ? <p className='meta'>Nobody&apos;s contract is up.</p> :
                <ul className='resign-list'>
                    {expiring.map(p => {
                        const ask = askingContract(p, rules);
                        const tried = team.resignTries?.[p.id] === league.season.offseason?.stage;
                        return (
                            <li key={p.id}>
                                <div><b>{p.fName} {p.lName}</b> <span className='meta'>{positions[p.pos1 - 1]?.short} · age {p.age} · {p.overall} · last {formatMoney(p.contract.salary)}</span></div>
                                <div className='meta'>Market value about {formatMoney(ask.salary)} for {ask.years} year{ask.years === 1 ? '' : 's'}</div>
                                <OfferForm rules={rules} ask={ask} busy={busy === `resign-${p.id}`} disabled={tried || Boolean(busy)}
                                    label={tried ? 'Try again tomorrow' : 'Offer'}
                                    onOffer={(salary, years) => run(`resign-${p.id}`, 'resignPlayer', { playerId: p.id, salary, years },
                                        (res) => setAnswers({ ...answers, [p.id]: res }))} />
                                {answers[p.id] && <p className={`answer ${answers[p.id].accepted ? 'yes' : 'no'}`}>“{answers[p.id].reply}”</p>}
                            </li>
                        )
                    })}
                </ul>
            }
            <p className='meta'>Anyone you don&apos;t re-sign becomes a free agent.</p>
        </div>
    );
};

// Salary (in millions) and years.
const OfferForm = ({ rules, ask, busy, disabled, label, onOffer }) => {
    const [ salary, setSalary ] = useState((ask.salary / 1e6).toFixed(1));
    const [ years, setYears ] = useState(ask.years);
    const dollars = Math.round(Number(salary) * 10) * 100000;
    const valid = dollars >= rules.minSalary && dollars <= maxSalary(rules);
    return (
        <div className='offer-form'>
            <label>
                <span className='meta'>Salary ($M)</span>
                <input type='number' inputMode='decimal' step='0.1' min={rules.minSalary / 1e6} max={maxSalary(rules) / 1e6}
                    value={salary} onChange={(e) => setSalary(e.target.value)} />
            </label>
            <label>
                <span className='meta'>Years</span>
                <select value={years} onChange={(e) => setYears(Number(e.target.value))}>
                    {Array.from({ length: MAX_YEARS }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
                </select>
            </label>
            <button className={`g-button primary small-btn ${disabled || !valid ? 'disabled' : ''}`} disabled={disabled || !valid}
                onClick={() => onOffer(dollars, years)}>
                {busy ? <ThreeDotsLoader /> : label}
            </button>
        </div>
    );
};

const FaCard = ({ c, teams, teamLink, myTeam, rules, day, inSeason, room, pitchesLeft, busy, run, onSign, log, reply, setReply }) => {
    const myId = myTeam?.id;
    const mine = c.offers?.[myId];
    const from = c.fromTeamId && teams?.[c.fromTeamId];
    const offering = Object.keys(c.offers ?? {});
    return (
        <div className={`g-card fa-card ${mine ? 'mine' : ''}`}>
            <div className='fa-top'>
                <div>
                    <div className='fa-name'>{c.fName} {c.lName}</div>
                    <div className='meta'>{positions[c.pos1 - 1]?.short}{c.pos2 ? `/${positions[c.pos2 - 1]?.short}` : ''} · age {c.age} · {Math.floor(c.height / 12)}&apos;{c.height % 12}&quot;</div>
                </div>
                <div className='fa-ovr'><span>{c.overall}</span><span className='meta'>OVR</span></div>
            </div>
            <div className='fa-from'>
                {from ? <><TeamBadge team={from} size='sm' /> <span>Last with <Link to={teamLink(c.fromTeamId)}>{from.name}</Link></span></> : <span className='meta'>New to the league</span>}
            </div>
            <div className='meta'>Off {c.off_quality} · Def {c.def_quality}{c.line?.gp ? ` · ${c.line.ppg} pts · ${c.line.rpg} reb · ${c.line.apg} ast` : ''}</div>
            <div className='fa-ask'>Asking <b>{formatMoney(c.ask.salary)}</b> for {c.ask.years} year{c.ask.years === 1 ? '' : 's'}</div>
            {offering.length > 0 &&
                <div className='interest-row'>
                    {offering.sort((a, b) => (LEVEL_ORDER[c.interest?.[a]] ?? 3) - (LEVEL_ORDER[c.interest?.[b]] ?? 3)).map(tid => (
                        <span key={tid} className={`interest ${c.interest?.[tid] ?? ''} ${tid === myId ? 'mine' : ''}`}>
                            {teams?.[tid]?.abbr ?? tid} {c.interest?.[tid] && <span>{c.interest[tid]}</span>}
                        </span>
                    ))}
                </div>
            }
            {mine && <div className='fa-mine'>Your offer: <b>{formatMoney(mine.salary)}</b> for {mine.years} year{mine.years === 1 ? '' : 's'}</div>}
            {(log.length > 0 || reply) &&
                <ul className='replies'>
                    {log.map((l, i) => <li key={i}><span className='meta'>Day {l.day} · {FA_PITCHES.find(p => p.key === l.type)?.label}:</span> “{l.reply}”</li>)}
                    {reply && !log.some(l => l.reply === reply.reply && l.type === reply.type) &&
                        <li><span className='meta'>{FA_PITCHES.find(p => p.key === reply.type)?.label}:</span> “{reply.reply}”</li>}
                </ul>
            }
            {myTeam && (day > 0 || inSeason) &&
            <div className='fa-actions'>
            {day > 0 &&
                <>
                    <OfferForm rules={rules} ask={mine ?? c.ask} busy={busy === `offer-${c.id}`} disabled={Boolean(busy)}
                        label={mine ? 'Change offer' : 'Make offer'}
                        onOffer={(salary, years) => run(`offer-${c.id}`, 'faOffer', { playerId: c.id, salary, years })} />
                    {mine && <button className='g-button small-btn' disabled={Boolean(busy)} onClick={() => run(`offer-${c.id}`, 'faOffer', { playerId: c.id, withdraw: true })}>Withdraw offer</button>}
                    <div className='pitch-row'>
                        {FA_PITCHES.map(p => (
                            <button key={p.key} className={`g-button small-btn ${busy || pitchesLeft <= 0 ? 'disabled' : ''}`} disabled={Boolean(busy) || pitchesLeft <= 0} title={p.text}
                                onClick={() => run(`pitch-${c.id}`, 'faPitch', { playerId: c.id, type: p.key }, (res) => setReply({ type: p.key, reply: res.reply }))}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                </>
            }
            {inSeason &&
                <button className={`g-button primary small-btn ${busy || room < c.ask.salary ? 'disabled' : ''}`} disabled={Boolean(busy) || room < c.ask.salary}
                    onClick={() => onSign(c)}>
                    {busy === `sign-${c.id}` ? <ThreeDotsLoader /> : room < c.ask.salary ? 'Not enough cap room' : `Sign for ${formatMoney(c.ask.salary)}`}
                </button>
            }
            </div>
            }
        </div>
    );
};

export default FrontOffice
